/**
 * @plt/emails — the email set of handover rev7 §11.5, on the one layout of
 * §11.6, with the first-draft Hungarian copy §19.5 step 1 asks for.
 *
 * One entry point, `render(payload, options)`, used by two callers that must
 * never disagree: the `send_outbox` worker, which renders what it sends, and
 * the `/staff/emails` preview page, which renders what Barna reviews. If the
 * preview and the send came from different code, the copy review of §19.5
 * step 3 would be a review of something nobody receives.
 *
 * Nothing here reads the database, the clock or the environment. A template
 * is a pure function of one `EmailPayload`, which is what makes every one of
 * them testable without a stack.
 */

export * from './payload.ts'
export { BRAND_TAGLINE, BRAND_TITLE, brandDescription, brandTagline, brandTitle } from './brand.ts'
export * from './format.ts'
export {
  renderEmail,
  escape,
  COLOURS,
  FONT_STACK,
  WIDTH_PX,
  CUSTOMER_WIDTH_PX,
  renderText,
  type Block,
  type Cell,
  type Email,
  type RenderOptions,
  type Tone,
} from './layout.ts'
export {
  osszesites,
  ALLERGENS_BEING_VERIFIED,
  eventLine,
  foodTable,
  serviceTable,
  totalsBlock,
  allergenTextLong,
  allergensAreVerified,
  shown,
  CATEGORY_ORDER,
  CATEGORY_LABEL,
  SERVICE_LABEL,
  GLUTEN_FREE_NOTICE,
  ALLERGEN_EXAMPLE_NOTICE,
  PLANT_BASED_LINE,
  markedName,
  nameMarks,
  /* Run 19 §4: the meal note the payload carries, and the occasion name
     with it beside. One source for every surface. */
  mealNoteOf,
  withMealNote,
  PLANT_BASED_LOOKALIKE_NOTE,
} from './osszesites.ts'
export { TEMPLATES, EMAIL_CODES, CODE_TITLE, DRAFT_CODES, COPY_SLOTS, copySlot, fallbackTemplate, type Template } from './templates.ts'
export * from './kitchenDoc.ts'
export { STAFF_ACTIONS, OPEN_IN_CONSOLE, CONSOLE_HOME, staffLinkTargets, staffLinks, type StaffTarget } from './consoleLinks.ts'
export { CUSTOMER_EN, summaryEn, ftEn, dateEn, dateTimeEn, longDateEn } from './english.ts'
export * from './legal.ts'
export { TEST_ORDER_LINE, TEST_ORDER_SUBJECT_PREFIX, isTestPayload, testOrderEmail } from './testOrder.ts'
export { sampleOrder, samplePayload, SAMPLE_BANNER, TEST_SYSTEM_EMAIL_BANNER, emailBanner } from './sample.ts'

import type { Email, RenderOptions } from './layout.ts'
import { CUSTOMER_WIDTH_PX, renderEmail, renderText, WIDTH_PX } from './layout.ts'
import type { EmailPayload } from './payload.ts'
import { CUSTOMER_EN } from './english.ts'
import { withStaffLinks } from './consoleLinks.ts'
import { fallbackTemplate, TEMPLATES } from './templates.ts'
import { testOrderEmail } from './testOrder.ts'

export interface RenderedEmail {
  code: string
  subject: string
  preheader: string
  html: string
  /** Run 12 item 3c: the plain-text part, from the same blocks. */
  text: string
}

/** The blocks of one email, before they become HTML. */
export function buildEmail(payload: EmailPayload): Email {
  // Run 12 item B (DECIDED 20261012): an order placed in English gets the
  // English customer emails; staff, finance and kitchen emails stay Hungarian.
  const english = payload.lang === 'en' && payload.audience === 'customer' ? CUSTOMER_EN[payload.code] : undefined
  const template = english ?? TEMPLATES[payload.code] ?? fallbackTemplate
  // SimplePay test prompt §2: a test order's email says so, on every audience.
  const email = testOrderEmail(template(payload), payload)
  // Run 12 item G (DECIDED 20261012): a staff email links to its action,
  // near the top. Never a customer email (`withStaffLinks` checks the audience).
  return payload.audience === 'customer' ? email : { ...email, blocks: withStaffLinks(payload, email.blocks) }
}

/** Subject, HTML and text, as the sender stores and sends them. */
export function render(payload: EmailPayload, options: RenderOptions = {}): RenderedEmail {
  const email = buildEmail(payload)
  const layout: RenderOptions = {
    replyTo: options.replyTo ?? payload.site.reply_to ?? payload.site.catering_email,
    siteUrl: options.siteUrl ?? payload.site.site_url,
    driver: options.driver,
    banner: options.banner,
    lang: payload.lang,
    // Run 12 item 3c: 600 px, except the kitchen's documents (§11.6, 680).
    width: options.width ?? (payload.code.startsWith('K') ? WIDTH_PX : CUSTOMER_WIDTH_PX),
  }
  return {
    code: payload.code,
    subject: email.subject,
    preheader: email.preheader,
    html: renderEmail(email, layout),
    text: renderText(email, layout),
  }
}
